import { useEffect, useMemo, useState } from "react"
import { useForm } from "@inertiajs/react"
import { Switch } from "@headlessui/react"
import { PlusIcon, TrashIcon } from "@heroicons/react/20/solid"
import { AdminPromotion, PromotionTranslation } from "@/types/serializers"
import { Modals, useModals } from "@/hooks/useModals"
import Button from "@/components/Button"
import ErrorMessage from "@/components/ErrorMessage"
import Modal from "@/components/Modal"
import Input from "@/components/Input"
import { localeDetailFor, localeDetails } from "@/utils/promotionLocale"
import clsx from "clsx"

type TranslationInput = Omit<PromotionTranslation, "id"> & {
  id?: number
  _destroy?: boolean
}

type PromotionInputs = {
  title: string
  description: string
  content: string
  default_locale: string
  translations_attributes: TranslationInput[]
  slug: string
  hashtag: string
  start_at: string
  end_at: string
  featured: boolean
  auto_discovery_enabled: boolean
  x_search_query: string
}

const emptyTranslation = (locale: string): TranslationInput => ({
  locale,
  title: "",
  description: "",
  content: "",
})

const initialData: PromotionInputs = {
  title: "",
  description: "",
  content: "",
  default_locale: "en",
  translations_attributes: [emptyTranslation("en")],
  slug: "",
  hashtag: "",
  start_at: "",
  end_at: "",
  featured: false,
  auto_discovery_enabled: false,
  x_search_query: "",
}

const PromotionModal = ({
  selectedPromotion,
  setSelectedPromotion,
}: {
  selectedPromotion: AdminPromotion | null
  setSelectedPromotion: (promotion: AdminPromotion | null) => void
}) => {
  const { activeModal, closeModal } = useModals()
  const isOpen = activeModal === Modals.Promotion
  const [activeLocale, setActiveLocale] = useState("en")
  const [localeToAdd, setLocaleToAdd] = useState("es")

  const { data, setData, patch, post, processing, errors, reset } =
    useForm<PromotionInputs>(initialData)

  const visibleTranslations = useMemo(
    () => data.translations_attributes.filter(({ _destroy }) => !_destroy),
    [data.translations_attributes]
  )
  const activeTranslation = visibleTranslations.find(
    ({ locale }) => locale === activeLocale
  )
  const availableToAdd = Object.keys(localeDetails).filter(
    (locale) =>
      !visibleTranslations.some((translation) => translation.locale === locale)
  )

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    if (selectedPromotion) {
      patch(`/admin/promotions/${selectedPromotion.id}`, {
        async: true,
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => handleClose(),
      })
    } else {
      post("/admin/promotions", {
        async: true,
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => handleClose(),
      })
    }
  }

  useEffect(() => {
    if (selectedPromotion) {
      const translations = selectedPromotion.translations.map(
        (translation) => ({
          ...translation,
          _destroy: false,
        })
      )
      const defaultLocale = selectedPromotion.default_locale

      setData({
        title: selectedPromotion.title,
        description: selectedPromotion.description,
        content: selectedPromotion.content,
        default_locale: defaultLocale,
        translations_attributes:
          translations.length > 0
            ? translations
            : [
                {
                  locale: defaultLocale,
                  title: selectedPromotion.title,
                  description: selectedPromotion.description,
                  content: selectedPromotion.content,
                },
              ],
        slug: selectedPromotion.slug,
        hashtag: selectedPromotion.hashtag,
        start_at: selectedPromotion.start_at,
        end_at: selectedPromotion.end_at,
        featured: selectedPromotion.featured,
        auto_discovery_enabled: selectedPromotion.auto_discovery_enabled,
        x_search_query: selectedPromotion.x_search_query ?? "",
      })
      setActiveLocale(defaultLocale)
    } else {
      reset()
      setActiveLocale("en")
    }
  }, [selectedPromotion, setData, reset])

  useEffect(() => {
    if (availableToAdd.length > 0 && !availableToAdd.includes(localeToAdd)) {
      setLocaleToAdd(availableToAdd[0])
    }
  }, [availableToAdd, localeToAdd])

  const handleClose = () => {
    closeModal()
    setTimeout(() => setSelectedPromotion(null), 300)
  }

  const updateTranslation = (
    locale: string,
    field: "title" | "description" | "content",
    value: string
  ) => {
    const translations = data.translations_attributes.map((translation) =>
      translation.locale === locale
        ? { ...translation, [field]: value }
        : translation
    )
    setData("translations_attributes", translations)

    if (locale === data.default_locale) {
      setData(field, value)
    }
  }

  const addLanguage = () => {
    if (!localeToAdd || !availableToAdd.includes(localeToAdd)) return

    setData("translations_attributes", [
      ...data.translations_attributes,
      emptyTranslation(localeToAdd),
    ])
    setActiveLocale(localeToAdd)
  }

  const removeLanguage = (locale: string) => {
    if (locale === data.default_locale) return

    const translations = data.translations_attributes.flatMap((translation) => {
      if (translation.locale !== locale) return [translation]
      return translation.id ? [{ ...translation, _destroy: true }] : []
    })
    setData("translations_attributes", translations)
    setActiveLocale(data.default_locale)
  }

  const changeDefaultLocale = (locale: string) => {
    const translation = visibleTranslations.find(
      (item) => item.locale === locale
    )
    if (!translation) return

    setData({
      ...data,
      default_locale: locale,
      title: translation.title,
      description: translation.description,
      content: translation.content,
    })
    setActiveLocale(locale)
  }

  const translationError = (field: string) =>
    (errors as Record<string, string>)[`translations.${field}`] ??
    (errors as Record<string, string>)[`translations_attributes.${field}`]

  return (
    <Modal
      show={isOpen}
      onClose={handleClose}
      title={selectedPromotion ? "Edit promotion" : "Add new promotion"}
      maxWidthClassName="sm:max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="mt-4 space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Slug"
            name="slug"
            required
            placeholder="event-name-2026"
            value={data.slug}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setData("slug", event.target.value)
            }
            error={errors.slug}
          />

          <Input
            label="Hashtag"
            name="hashtag"
            required
            placeholder="#EventName2026"
            value={data.hashtag}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setData("hashtag", event.target.value)
            }
            error={errors.hashtag}
          />

          <Input
            label="Start date"
            name="start_at"
            description="UTC in ISO 8601 format."
            placeholder="2026-07-18T14:00:00Z"
            required
            value={data.start_at}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setData("start_at", event.target.value)
            }
            error={errors.start_at}
          />

          <Input
            label="End date"
            name="end_at"
            description="UTC in ISO 8601 format."
            placeholder="2026-07-25T22:00:00Z"
            required
            value={data.end_at}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setData("end_at", event.target.value)
            }
            error={errors.end_at}
          />
        </div>

        <div className="border-t border-stone-200 pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-base font-bold text-stone-950">Languages</h3>
              <p className="mt-1 text-sm text-stone-500">
                Each language replaces the complete public promotion copy.
              </p>
            </div>
            <label className="text-sm font-medium text-stone-900">
              Default language
              <select
                value={data.default_locale}
                onChange={(event) => changeDefaultLocale(event.target.value)}
                className="focus:ring-chessdrop-600 mt-1 block h-10 rounded-lg border-0 bg-white py-2 pr-9 pl-3 text-sm shadow-xs ring-1 ring-stone-300 ring-inset focus:ring-2 focus:ring-inset"
              >
                {visibleTranslations.map(({ locale }) => (
                  <option key={locale} value={locale}>
                    {localeDetailFor(locale).label}
                  </option>
                ))}
              </select>
              {errors.default_locale ? (
                <ErrorMessage
                  className="mt-1"
                  message={errors.default_locale}
                />
              ) : null}
            </label>
          </div>

          <div className="mt-5 flex max-w-full gap-1 overflow-x-auto border-b border-stone-200">
            {visibleTranslations.map(({ locale }) => (
              <button
                key={locale}
                type="button"
                onClick={() => setActiveLocale(locale)}
                className={clsx(
                  "h-10 shrink-0 border-b-2 px-3 text-sm font-semibold",
                  locale === activeLocale
                    ? "border-chessdrop-500 text-stone-950"
                    : "border-transparent text-stone-500 hover:text-stone-900"
                )}
              >
                {localeDetailFor(locale).label}
              </button>
            ))}
          </div>

          {activeTranslation && (
            <div className="mt-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <span className="text-sm font-semibold text-stone-500">
                  {localeDetailFor(activeTranslation.locale).label}
                </span>
                <button
                  type="button"
                  title="Remove language"
                  aria-label={`Remove ${localeDetailFor(activeTranslation.locale).label}`}
                  disabled={activeTranslation.locale === data.default_locale}
                  onClick={() => removeLanguage(activeTranslation.locale)}
                  className="focus-visible:outline-chessdrop-500 rounded-md p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600 focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <TrashIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>

              <Input
                label="Title"
                name={`translation_${activeTranslation.locale}_title`}
                required
                value={activeTranslation.title}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  updateTranslation(
                    activeTranslation.locale,
                    "title",
                    event.target.value
                  )
                }
                error={translationError("title")}
              />

              <Input
                label="Description"
                name={`translation_${activeTranslation.locale}_description`}
                type="textarea"
                rows={3}
                required
                value={activeTranslation.description}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  updateTranslation(
                    activeTranslation.locale,
                    "description",
                    event.target.value
                  )
                }
                error={translationError("description")}
              />

              <Input
                label="Rules and content"
                name={`translation_${activeTranslation.locale}_content`}
                description="Markdown format. The participation form is placed above this content automatically."
                type="textarea"
                rows={12}
                required
                value={activeTranslation.content}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  updateTranslation(
                    activeTranslation.locale,
                    "content",
                    event.target.value
                  )
                }
                error={translationError("content")}
              />
            </div>
          )}

          {availableToAdd.length > 0 && (
            <div className="mt-5 flex items-end gap-2">
              <label className="flex-1 text-sm font-medium text-stone-900">
                Add language
                <select
                  value={localeToAdd}
                  onChange={(event) => setLocaleToAdd(event.target.value)}
                  className="focus:ring-chessdrop-600 mt-1 block h-10 w-full rounded-lg border-0 bg-white py-2 pr-9 pl-3 text-sm shadow-xs ring-1 ring-stone-300 ring-inset focus:ring-2 focus:ring-inset"
                >
                  {availableToAdd.map((locale) => (
                    <option key={locale} value={locale}>
                      {localeDetailFor(locale).label}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                type="button"
                style="secondary"
                size="sm"
                className="rounded-lg"
                onClick={addLanguage}
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                Add
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-5 border-t border-stone-200 pt-6 sm:grid-cols-2">
          <div className="space-y-1">
            <div className="text-sm leading-6 font-medium text-stone-900">
              Featured
            </div>
            <Switch
              checked={data.featured}
              onChange={(checked) => setData("featured", checked)}
              className={clsx(
                data.featured ? "bg-green-600" : "bg-stone-200",
                "focus:ring-chessdrop-600 relative inline-flex h-6 w-11 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none"
              )}
            >
              <span className="sr-only">Toggle featured</span>
              <span
                aria-hidden="true"
                className={clsx(
                  data.featured ? "translate-x-5" : "translate-x-0",
                  "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition"
                )}
              />
            </Switch>
          </div>

          <div className="space-y-1">
            <div className="text-sm leading-6 font-medium text-stone-900">
              Auto-detect X posts
            </div>
            <Switch
              checked={data.auto_discovery_enabled}
              onChange={(checked) => setData("auto_discovery_enabled", checked)}
              className={clsx(
                data.auto_discovery_enabled ? "bg-green-600" : "bg-stone-200",
                "focus:ring-chessdrop-600 relative inline-flex h-6 w-11 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none"
              )}
            >
              <span className="sr-only">Toggle X post auto-detection</span>
              <span
                aria-hidden="true"
                className={clsx(
                  data.auto_discovery_enabled
                    ? "translate-x-5"
                    : "translate-x-0",
                  "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition"
                )}
              />
            </Switch>
          </div>
        </div>

        <Input
          label="X search query"
          name="x_search_query"
          description='Optional. Leave blank to search for the hashtag plus links to "chessdrop.com/games".'
          type="textarea"
          rows={2}
          value={data.x_search_query}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setData("x_search_query", event.target.value)
          }
          error={errors.x_search_query}
        />

        <Button loading={processing} type="submit" fullWidth>
          Save promotion
        </Button>
      </form>
    </Modal>
  )
}

export default PromotionModal
